import { Component, OnInit, OnDestroy, inject, ChangeDetectorRef } from '@angular/core';
import { interval, Subscription } from 'rxjs';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDialogModule, MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatChipsModule } from '@angular/material/chips';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDividerModule } from '@angular/material/divider';
import { WebPageService } from '../../services/web-page.service';
import { AuditService } from '../../services/audit.service';
import { WebPage } from '../../models';
import { PageDialogComponent } from './page-dialog.component';
import { AuditHistoryDialogComponent } from './audit-history-dialog.component';
import { ConfirmDialogComponent } from '../shared/confirm-dialog.component';

@Component({
  selector: 'app-pages',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatCardModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatChipsModule,
    MatMenuModule,
    MatProgressBarModule,
    MatSnackBarModule,
    MatTooltipModule,
    MatDividerModule,
  ],
  template: `
    <div class="pages-container">
      <div class="page-header">
        <h1>Páginas Web</h1>
        <button mat-raised-button color="primary" (click)="openDialog()">
          <mat-icon>add</mat-icon>
          Nueva Página
        </button>
      </div>

      <mat-card class="data-table-container">
        <div class="table-header">
          <mat-form-field appearance="outline" class="search-field">
            <mat-label>Buscar páginas</mat-label>
            <input
              matInput
              [(ngModel)]="searchTerm"
              (ngModelChange)="applyFilters()"
              placeholder="URL o nombre..."
            />
            <mat-icon matSuffix>search</mat-icon>
          </mat-form-field>

          <mat-form-field appearance="outline" class="filter-field">
            <mat-label>Estado</mat-label>
            <mat-select [(ngModel)]="statusFilter" (ngModelChange)="applyFilters()">
              <mat-option value="">Todos</mat-option>
              <mat-option value="active">Activos</mat-option>
              <mat-option value="inactive">Inactivos</mat-option>
              <mat-option value="pending">Pendientes</mat-option>
            </mat-select>
          </mat-form-field>
        </div>

        <table mat-table [dataSource]="filteredPages" class="pages-table">
          <ng-container matColumnDef="name">
            <th mat-header-cell *matHeaderCellDef>Página</th>
            <td mat-cell *matCellDef="let page">
              <div class="page-cell">
                <div class="page-icon" [ngClass]="page.status">
                  <mat-icon>language</mat-icon>
                </div>
                <div class="page-info">
                  <span class="page-name">{{ page.name }}</span>
                  <a class="page-url" [href]="page.url" target="_blank">{{ page.url }}</a>
                </div>
              </div>
            </td>
          </ng-container>

          <ng-container matColumnDef="status">
            <th mat-header-cell *matHeaderCellDef>Estado</th>
            <td mat-cell *matCellDef="let page">
              <span class="status-badge" [ngClass]="page.status">
                {{ getStatusLabel(page.status) }}
              </span>
            </td>
          </ng-container>

          <ng-container matColumnDef="securityScore">
            <th mat-header-cell *matHeaderCellDef>Score</th>
            <td mat-cell *matCellDef="let page">
              <div class="score-cell" *ngIf="page.securityScore !== undefined">
                <mat-progress-bar
                  mode="determinate"
                  [value]="page.securityScore"
                  [color]="getScoreColor(page.securityScore)"
                >
                </mat-progress-bar>
                <span [ngClass]="getScoreClass(page.securityScore)">{{ page.securityScore }}%</span>
              </div>
              <span *ngIf="page.securityScore === undefined" class="no-data">Sin auditar</span>
            </td>
          </ng-container>

          <ng-container matColumnDef="vulnerabilities">
            <th mat-header-cell *matHeaderCellDef>Vulnerabilidades</th>
            <td mat-cell *matCellDef="let page">
              <div class="vuln-badges" *ngIf="page.vulnerabilities">
                <span
                  class="vuln-badge critical"
                  *ngIf="page.vulnerabilities.critical > 0"
                  matTooltip="Críticas"
                >
                  {{ page.vulnerabilities.critical }}
                </span>
                <span
                  class="vuln-badge high"
                  *ngIf="page.vulnerabilities.high > 0"
                  matTooltip="Altas"
                >
                  {{ page.vulnerabilities.high }}
                </span>
                <span
                  class="vuln-badge medium"
                  *ngIf="page.vulnerabilities.medium > 0"
                  matTooltip="Medias"
                >
                  {{ page.vulnerabilities.medium }}
                </span>
                <span
                  class="vuln-badge low"
                  *ngIf="page.vulnerabilities.low > 0"
                  matTooltip="Bajas"
                >
                  {{ page.vulnerabilities.low }}
                </span>
                <span *ngIf="getTotalVulns(page) === 0" class="no-vulns">Sin vulnerabilidades</span>
              </div>
              <span *ngIf="!page.vulnerabilities" class="no-data">-</span>
            </td>
          </ng-container>

          <ng-container matColumnDef="lastAudit">
            <th mat-header-cell *matHeaderCellDef>Última Auditoría</th>
            <td mat-cell *matCellDef="let page">
              {{ page.lastAudit ? (page.lastAudit | date: 'dd/MM/yyyy') : 'Nunca' }}
            </td>
          </ng-container>

          <ng-container matColumnDef="nextAudit">
            <th mat-header-cell *matHeaderCellDef>Próxima Auditoría</th>
            <td mat-cell *matCellDef="let page">
              <span [ngClass]="{ overdue: isOverdue(page.nextAudit) }">
                {{ page.nextAudit ? (page.nextAudit | date: 'dd/MM/yyyy') : '-' }}
              </span>
            </td>
          </ng-container>

          <ng-container matColumnDef="owner">
            <th mat-header-cell *matHeaderCellDef>Responsable</th>
            <td mat-cell *matCellDef="let page">{{ page.owner }}</td>
          </ng-container>

          <ng-container matColumnDef="actions">
            <th mat-header-cell *matHeaderCellDef>Acciones</th>
            <td mat-cell *matCellDef="let page">
              <button mat-icon-button [matMenuTriggerFor]="menu" matTooltip="Opciones">
                <mat-icon>more_vert</mat-icon>
              </button>
              <mat-menu #menu="matMenu">
                <button mat-menu-item (click)="openDialog(page)">
                  <mat-icon>edit</mat-icon>
                  <span>Editar</span>
                </button>
                <button mat-menu-item (click)="runAudit(page)">
                  <mat-icon>play_circle</mat-icon>
                  <span>Ejecutar Auditoría</span>
                </button>
                <button mat-menu-item (click)="viewAuditHistory(page)">
                  <mat-icon>assessment</mat-icon>
                  <span>Ver Reportes</span>
                </button>
                <mat-divider></mat-divider>
                <button mat-menu-item (click)="deletePage(page)" class="delete-option">
                  <mat-icon>delete</mat-icon>
                  <span>Eliminar</span>
                </button>
              </mat-menu>
            </td>
          </ng-container>

          <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
          <tr mat-row *matRowDef="let row; columns: displayedColumns"></tr>
        </table>

        <div class="table-footer">
          <span class="results-count">{{ filteredPages.length }} páginas registradas</span>
        </div>
      </mat-card>
    </div>
  `,
  styles: [
    `
      .pages-container {
        max-width: 1400px;
        margin: 0 auto;
      }

      .page-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 24px;

        h1 {
          font-family: var(--font-display);
          font-size: 28px;
          font-weight: 700;
          letter-spacing: -0.02em;
          color: var(--text-primary);
          margin: 0;
        }
      }

      .data-table-container {
        overflow: hidden;
        border-radius: var(--radius-lg);
        border: 1px solid rgba(0, 0, 0, 0.05);
        box-shadow: var(--shadow-sm);
      }

      .pages-table {
        overflow-x: auto;
      }

      @media (max-width: 768px) {
        .table-header {
          flex-direction: column;
          padding: 16px;
        }

        .search-field {
          max-width: 100%;
        }
      }

      .table-header {
        display: flex;
        gap: 16px;
        padding: 16px 24px;
        border-bottom: 1px solid var(--border-color);
      }

      .search-field {
        flex: 1;
        max-width: 400px;
      }

      .filter-field {
        width: 150px;
      }

      .pages-table {
        width: 100%;
      }

      .page-cell {
        display: flex;
        align-items: center;
        gap: 12px;
      }

      .page-icon {
        width: 40px;
        height: 40px;
        border-radius: var(--radius-md);
        display: flex;
        align-items: center;
        justify-content: center;

        &.active {
          background: rgba(76, 175, 80, 0.1);
          color: #4caf50;
        }

        &.inactive {
          background: rgba(158, 158, 158, 0.1);
          color: #9e9e9e;
        }

        &.pending {
          background: rgba(255, 152, 0, 0.1);
          color: #ff9800;
        }
      }

      .page-info {
        display: flex;
        flex-direction: column;
      }

      .page-name {
        font-weight: 500;
        color: rgba(0, 0, 0, 0.87);
      }

      .page-url {
        font-size: 12px;
        color: var(--primary-color);
        text-decoration: none;

        &:hover {
          text-decoration: underline;
        }
      }

      .status-badge {
        padding: 4px 12px;
        border-radius: 999px;
        font-size: 12px;
        font-weight: 600;

        &.active {
          background: rgba(76, 175, 80, 0.1);
          color: #2e7d32;
        }

        &.inactive {
          background: rgba(158, 158, 158, 0.1);
          color: #616161;
        }

        &.pending {
          background: rgba(255, 152, 0, 0.1);
          color: #e65100;
        }
      }

      .score-cell {
        display: flex;
        align-items: center;
        gap: 12px;

        mat-progress-bar {
          width: 80px;
        }

        span {
          font-weight: 500;
          min-width: 40px;

          &.high {
            color: #2e7d32;
          }
          &.medium {
            color: #f9a825;
          }
          &.low {
            color: #c62828;
          }
        }
      }

      .vuln-badges {
        display: flex;
        gap: 4px;
      }

      .vuln-badge {
        padding: 2px 8px;
        border-radius: 999px;
        font-size: 11px;
        font-weight: 600;

        &.critical {
          background: rgba(183, 28, 28, 0.1);
          color: #b71c1c;
        }

        &.high {
          background: rgba(230, 81, 0, 0.1);
          color: #e65100;
        }

        &.medium {
          background: rgba(249, 168, 37, 0.1);
          color: #f9a825;
        }

        &.low {
          background: rgba(46, 125, 50, 0.1);
          color: #2e7d32;
        }
      }

      .no-vulns {
        font-size: 12px;
        color: #2e7d32;
      }

      .no-data {
        font-size: 12px;
        color: rgba(0, 0, 0, 0.38);
      }

      .overdue {
        color: #c62828;
        font-weight: 500;
      }

      .delete-option {
        color: #c62828;
      }

      .table-footer {
        padding: 16px 24px;
        border-top: 1px solid var(--border-color);
      }

      .results-count {
        font-size: 13px;
        color: rgba(0, 0, 0, 0.54);
      }

      ::ng-deep .mat-mdc-form-field-subscript-wrapper {
        display: none;
      }
    `,
  ],
})
export class PagesComponent implements OnInit, OnDestroy {
  pages: WebPage[] = [];
  filteredPages: WebPage[] = [];
  searchTerm = '';
  statusFilter = '';
  private pollSubscription?: Subscription;
  private hasPendingAudit = false;
  displayedColumns = [
    'name',
    'status',
    'securityScore',
    'vulnerabilities',
    'lastAudit',
    'nextAudit',
    'owner',
    'actions',
  ];

  private dialog = inject(MatDialog);
  private snackBar = inject(MatSnackBar);
  private webPageService = inject(WebPageService);
  private auditService = inject(AuditService);
  private cdr = inject(ChangeDetectorRef);

  ngOnInit() {
    this.loadPages();
  }

  ngOnDestroy() {
    this.stopPolling();
  }

  private startPollingIfNeeded() {
    if (this.hasPendingAudit) {
      if (!this.pollSubscription) {
        this.pollSubscription = interval(5000).subscribe(() => {
          this.webPageService.getMyPages().subscribe({
            next: (pages) => {
              this.pages = pages;
              this.applyFilters();
              this.cdr.detectChanges();
            },
            error: () => {
              this.stopPolling();
            }
          });
        });
      }
    } else {
      this.stopPolling();
    }
  }

  private stopPolling() {
    if (this.pollSubscription) {
      this.pollSubscription.unsubscribe();
      this.pollSubscription = undefined;
    }
  }

  loadPages() {
    this.webPageService.getMyPages().subscribe({
      next: (pages) => {
        this.pages = pages;
        this.applyFilters();
        this.cdr.detectChanges();
      },
      error: (error) => {
        this.snackBar.open('Error al cargar las páginas', 'Reintentar', { duration: 6000 })
          .onAction()
          .subscribe(() => this.loadPages());
      }
    });
  }

  applyFilters() {
    this.filteredPages = this.pages.filter((page) => {
      const matchesSearch =
        !this.searchTerm ||
        page.name.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        page.url.toLowerCase().includes(this.searchTerm.toLowerCase());

      const matchesStatus = !this.statusFilter || page.status === this.statusFilter;

      return matchesSearch && matchesStatus;
    });
  }

  getStatusLabel(status: string): string {
    const labels: Record<string, string> = {
      active: 'Activo',
      inactive: 'Inactivo',
      pending: 'Pendiente',
    };
    return labels[status] || status;
  }

  getScoreColor(score: number): 'primary' | 'accent' | 'warn' {
    if (score >= 80) return 'primary';
    if (score >= 60) return 'accent';
    return 'warn';
  }

  getScoreClass(score: number): string {
    if (score >= 80) return 'high';
    if (score >= 60) return 'medium';
    return 'low';
  }

  getTotalVulns(page: WebPage): number {
    if (!page.vulnerabilities) return 0;
    return (
      page.vulnerabilities.critical +
      page.vulnerabilities.high +
      page.vulnerabilities.medium +
      page.vulnerabilities.low
    );
  }

  isOverdue(date?: Date): boolean {
    if (!date) return false;
    return new Date(date) < new Date();
  }

  openDialog(page?: WebPage) {
    const dialogRef = this.dialog.open(PageDialogComponent, {
      width: '600px',
      data: { page },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        const request = {
          url: result.url,
          name: result.name,
        };

        if (page) {
          this.webPageService.updatePage(page.id, request).subscribe({
            next: () => {
              this.snackBar.open('Página actualizada correctamente', 'Cerrar', { duration: 3000 });
              this.loadPages();
            },
            error: (error) => {
              this.snackBar.open('Error al actualizar la página', 'Cerrar', { duration: 5000 });
            }
          });
        } else {
          this.webPageService.createPage(request).subscribe({
            next: () => {
              this.snackBar.open('Página registrada correctamente', 'Cerrar', { duration: 3000 });
              this.loadPages();
            },
            error: (error) => {
              this.snackBar.open('Error al registrar la página', 'Cerrar', { duration: 5000 });
            }
          });
        }
      }
    });
  }

  runAudit(page: WebPage) {
    this.snackBar.open(`Encolando auditoría de "${page.name}"...`, 'Cerrar', { duration: 3000 });
    this.auditService.createAudit({ pageId: page.id }).subscribe({
      next: (response: any) => {
        this.hasPendingAudit = true;
        const message = response?.message || `Auditoría de "${page.name}" encolada`;
        this.snackBar.open(message, 'Cerrar', { duration: 4000 });
        this.startPollingIfNeeded();
      },
      error: (error) => {
        const message = error?.error?.message || 'Error al encolar la auditoría';
        this.snackBar.open(message, 'Cerrar', { duration: 5000 });
      }
    });
  }

  viewAuditHistory(page: WebPage) {
    this.dialog.open(AuditHistoryDialogComponent, {
      width: '950px',
      maxHeight: '80vh',
      data: {
        pageId: page.id,
        pageName: page.name
      }
    });
  }

  deletePage(page: WebPage) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        title: 'Eliminar Página',
        message: `¿Estás seguro de que deseas eliminar la página "${page.name}"? Esta acción eliminará también el historial de auditorías.`,
        confirmText: 'Eliminar',
        cancelText: 'Cancelar',
        type: 'danger',
      },
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.webPageService.deletePage(page.id).subscribe({
          next: () => {
            this.snackBar.open('Página eliminada correctamente', 'Cerrar', { duration: 3000 });
            this.loadPages();
          },
          error: (error) => {
            this.snackBar.open('Error al eliminar la página', 'Cerrar', { duration: 5000 });
          }
        });
      }
    });
  }
}
